/**
 * Use Revision Tracking Hook (Combiner)
 *
 * Thin composition hook that wires together the focused sub-hooks:
 * - useRevisedText: text revision state and view mode
 * - useScoreCalculation: score delta logic
 * - useRevisionSession: session lifecycle (create/complete/abandon)
 * - useRevisionAnalytics: PostHog tracking for rewrite decisions
 * - useRevisionApi: server-side rescore calls
 *
 * Returns the EXACT same API as the original monolithic hook for
 * backward compatibility. All consumers continue to import from here.
 *
 * SECURITY NOTE (Section 6.8 - Cache Key Security):
 * Revision tracking data is NOT cached client-side (React Query).
 * All data flows directly to Supabase with user_id scoping via RLS policies.
 *
 * @see REVISION_TRACKING_DATA_MODEL.md for schema documentation
 */

import { useCallback, useEffect, useRef } from 'react';
import { trackEvent, type UserTier } from '@/lib/analytics/events';
import { useRevisedText } from './useRevisedText';
import { useScoreCalculation, RESCORE_THRESHOLD } from './useScoreCalculation';
import { useRevisionSession } from './useRevisionSession';
import { useRevisionAnalytics } from './useRevisionAnalytics';
import { useRevisionApi } from './useRevisionApi';
import { countByStatus } from './revision-utils';
import { getRevisedText } from '../utils/highlightText';
import type { InlineIssue, IssueStatus, ViewMode } from '../types';

// Re-export types and utilities for backward compatibility
export { calculateRevisedScore, scoreToDramaCategory, mapSeverity, mapStatusToDecision } from './useScoreCalculation';

interface UseRevisionTrackingOptions {
  message: string;
  initialIssues: InlineIssue[];
  analysisId: string;
  originalScore: number;
  originalSummary?: string;
  agentExecutionId?: string;
  modelId?: string;
  enabled?: boolean;
  tier?: UserTier;
  context?: string;
  onTrackingError?: (error: Error, context: string) => void;
}

interface UseRevisionTrackingReturn {
  issues: InlineIssue[];
  viewMode: ViewMode;
  revisedText: string;
  hasChanges: boolean;
  statusCounts: Record<IssueStatus, number>;
  setViewMode: (mode: ViewMode) => void;
  updateIssueStatus: (issueId: number, status: IssueStatus) => void;
  acceptAll: () => void;
  resetAll: () => void;
  modifyAndAcceptIssue: (issueId: number, modifiedText: string) => void;
  revisionId: string | null;
  isTracking: boolean;
  originalScore: number;
  revisedScore: number;
  scoreDelta: number;
  isRescoring: boolean;
}

/**
 * Hook for revision tracking with RLHF data collection
 *
 * Wraps useRevisedText to intercept status changes and record them
 * to the database for training data collection.
 */
export function useRevisionTracking({
  message,
  initialIssues,
  analysisId,
  originalScore,
  originalSummary,
  agentExecutionId,
  modelId,
  enabled = true,
  tier = 'free',
  context,
  onTrackingError,
}: UseRevisionTrackingOptions): UseRevisionTrackingReturn {
  // 1. Text revision state (accept/ignore workflow, revised text computation)
  const revisedTextHook = useRevisedText({ message, initialIssues });

  // 2. Score calculation (client-side estimate + server score refs)
  const { revisedScore, scoreDelta, serverScoreRef, serverCategoryRef } = useScoreCalculation({
    issues: revisedTextHook.issues,
    originalScore,
    totalIssues: initialIssues.length,
  });

  // 3. Session lifecycle (lazy create, complete, abandon)
  const session = useRevisionSession({
    analysisId,
    message,
    originalScore,
    originalSummary,
    agentExecutionId,
    modelId,
    enabled,
    tier,
    initialIssues,
    onTrackingError,
  });

  // 4. Analytics (rewrite event tracking)
  const analytics = useRevisionAnalytics({
    enabled,
    tier,
    totalIssues: initialIssues.length,
    ensureRevisionSession: session.ensureRevisionSession,
    issueDisplayTimesRef: session.issueDisplayTimesRef,
    decisionLatenciesRef: session.decisionLatenciesRef,
    revisionIdRef: session.revisionIdRef,
    onTrackingError,
  });

  // Compute accepted count for rescore threshold
  const acceptedIssueCount = countByStatus(revisedTextHook.issues, 'accepted');

  // 5. Server-side rescore API
  const { triggerRescore, isRescoring } = useRevisionApi({
    analysisId,
    originalScore,
    context,
    enabled,
    tier,
    acceptedIssueCount,
    totalIssues: initialIssues.length,
    serverScoreRef,
    serverCategoryRef,
    onTrackingError,
  });

  // --- Composed actions ---

  /** Update issue status with tracking */
  const updateIssueStatus = useCallback(
    (issueId: number, status: IssueStatus) => {
      const issueIndex = revisedTextHook.issues.findIndex((i) => i.id === issueId);
      const issue = issueIndex >= 0 ? revisedTextHook.issues[issueIndex] : null;

      // Update UI immediately (optimistic)
      revisedTextHook.updateIssueStatus(issueId, status);

      // Track in background (non-blocking)
      if (issue && status !== 'pending') {
        analytics.trackRewriteEvent({ ...issue, status }, status, issueIndex);
      }

      // DD-162: Trigger server rescore when accepted count crosses threshold
      if (status === 'accepted') {
        const currentAccepted = countByStatus(revisedTextHook.issues, 'accepted');
        const newAcceptedCount = currentAccepted + 1;

        if (newAcceptedCount >= RESCORE_THRESHOLD) {
          // Eagerly compute post-update revised text to avoid stale closure
          // (React batches state updates, so revisedTextHook.revisedText is pre-update)
          const updatedIssues = revisedTextHook.issues.map((i) =>
            i.id === issueId ? { ...i, status } : i
          );
          triggerRescore(getRevisedText(message, updatedIssues));
        }
      }
    },
    [revisedTextHook.issues, revisedTextHook.updateIssueStatus, analytics.trackRewriteEvent, triggerRescore, message]
  );

  /** Accept all pending issues with tracking */
  const acceptAll = useCallback(() => {
    const pendingIssues = revisedTextHook.issues
      .map((issue, index) => ({ issue, index }))
      .filter(({ issue }) => issue.status === 'pending');

    // Update UI immediately (optimistic)
    revisedTextHook.acceptAll();

    // Track "Accept All" analytics event
    if (pendingIssues.length > 0 && session.revisionIdRef.current) {
      trackEvent('Revision_Accept_All_Triggered', {
        revision_id: session.revisionIdRef.current,
        issues_count: pendingIssues.length,
        tier,
      });
    }

    // Track each in background (non-blocking)
    pendingIssues.forEach(({ issue, index }) => {
      analytics.trackRewriteEvent({ ...issue, status: 'accepted' }, 'accepted', index);
    });

    // DD-162: Trigger server rescore when accepting all (always >= threshold)
    const totalAccepted =
      countByStatus(revisedTextHook.issues, 'accepted') +
      pendingIssues.length;
    if (totalAccepted >= RESCORE_THRESHOLD) {
      // Eagerly compute post-update revised text to avoid stale closure
      // (React batches state updates, so revisedTextHook.revisedText is pre-update)
      const updatedIssues = revisedTextHook.issues.map((i) =>
        i.status === 'pending' ? { ...i, status: 'accepted' as const } : i
      );
      triggerRescore(getRevisedText(message, updatedIssues));
    }
  }, [revisedTextHook.issues, revisedTextHook.acceptAll, analytics.trackRewriteEvent, session.revisionIdRef, tier, triggerRescore, message]);

  /** Accept issue with user-modified text (DD-550) */
  const modifyAndAcceptIssue = useCallback(
    (issueId: number, modifiedText: string) => {
      const issueIndex = revisedTextHook.issues.findIndex((i) => i.id === issueId);
      const issue = issueIndex >= 0 ? revisedTextHook.issues[issueIndex] : null;
      revisedTextHook.updateIssueStatus(issueId, 'accepted');
      if (issue) {
        analytics.trackModifiedEvent(issue, modifiedText);
      }
    },
    [revisedTextHook.issues, revisedTextHook.updateIssueStatus, analytics.trackModifiedEvent]
  );

  /** Enhanced setViewMode that completes session when switching to revised */
  const setViewMode = useCallback(
    (mode: ViewMode) => {
      revisedTextHook.setViewMode(mode);

      if (mode === 'revised' && revisedTextHook.hasChanges) {
        session.completeSession(
          revisedTextHook.issues,
          revisedTextHook.revisedText,
          revisedTextHook.hasChanges,
          serverScoreRef.current,
        );
      }
    },
    [revisedTextHook.setViewMode, revisedTextHook.hasChanges, revisedTextHook.issues, revisedTextHook.revisedText, session.completeSession, serverScoreRef]
  );

  // Keep refs to latest values for cleanup (avoids re-running effect on every render)
  const issuesRef = useRef(revisedTextHook.issues);
  const revisedTextRef = useRef(revisedTextHook.revisedText);
  issuesRef.current = revisedTextHook.issues;
  revisedTextRef.current = revisedTextHook.revisedText;

  // Cleanup on unmount: abandon session if not completed
  useEffect(() => {
    return () => {
      if (session.revisionIdRef.current && enabled) {
        session.abandonSession(
          issuesRef.current,
          revisedTextRef.current,
        ).catch((err) => {
          console.error('[RevisionTracking] Unmount cleanup failed:', err);
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return {
    // Pass-through from useRevisedText
    issues: revisedTextHook.issues,
    viewMode: revisedTextHook.viewMode,
    revisedText: revisedTextHook.revisedText,
    hasChanges: revisedTextHook.hasChanges,
    statusCounts: revisedTextHook.statusCounts,
    resetAll: revisedTextHook.resetAll,

    // Enhanced with tracking
    setViewMode,
    updateIssueStatus,
    acceptAll,
    modifyAndAcceptIssue,

    // Tracking state
    revisionId: session.revisionIdRef.current,
    isTracking: analytics.isTrackingRef.current,

    // Phase 2: Score Delta Display (Gamification)
    originalScore: Math.round(originalScore),
    revisedScore,
    scoreDelta,

    // DD-162: Server-side rescore state (reactive via useState)
    isRescoring,
  };
}

export default useRevisionTracking;
